import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Upload, FileText, Download, Trash2, Plus, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  "Driver's License",
  'W9',
  'Voided Check',
  'Bank Statement',
  'Articles of Incorporation',
  'Custom',
];

export default function ContactDocumentsTab({ contactId, getAuthHeaders }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ docName: '', docType: "Driver's License", customType: '', notes: '', file: null });
  const fileRef = useRef(null);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/contacts/${contactId}/contact-documents`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setDocs(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (contactId) loadDocs(); }, [contactId]); // eslint-disable-line

  const handleUpload = async () => {
    if (!form.file) { toast.error('Select a file first'); return; }
    if (!form.docName.trim()) { toast.error('Enter a document name'); return; }
    if (form.docType === 'Custom' && !form.customType.trim()) { toast.error('Enter custom document type'); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document_name', form.docName.trim());
      fd.append('document_type', form.docType);
      fd.append('custom_type', form.customType.trim());
      fd.append('notes', form.notes.trim());
      fd.append('file', form.file);

      const res = await fetch(`${API}/contacts/${contactId}/contact-documents/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setDocs(prev => [data, ...prev]);
      setShowUploadForm(false);
      setForm({ docName: '', docType: "Driver's License", customType: '', notes: '', file: null });
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.document_name}"?`)) return;
    try {
      const res = await fetch(`${API}/contacts/${contactId}/contact-documents/${doc.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      setDocs(prev => prev.filter(d => d.id !== doc.id));
      toast.success('Document deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDownload = (doc) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename || doc.document_name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formatDate = (iso) => {
    try { return format(new Date(iso), 'MMM d, yyyy'); } catch { return iso || '—'; }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Documents</h3>
          <p className="text-xs text-slate-500 mt-0.5">{docs.length} document{docs.length !== 1 ? 's' : ''} attached</p>
        </div>
        <Button
          size="sm"
          onClick={() => setShowUploadForm(v => !v)}
          data-testid="add-contact-doc-btn"
          variant={showUploadForm ? 'outline' : 'default'}
        >
          {showUploadForm ? <><X className="h-4 w-4 mr-1.5" />Cancel</> : <><Plus className="h-4 w-4 mr-1.5" />Add Document</>}
        </Button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="border rounded-lg p-4 bg-slate-50 space-y-3" data-testid="contact-doc-upload-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Document Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.docName}
                onChange={e => setForm(p => ({ ...p, docName: e.target.value }))}
                placeholder="e.g. Business License 2026"
                data-testid="contact-doc-name-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Document Type</Label>
              <Select value={form.docType} onValueChange={v => setForm(p => ({ ...p, docType: v, customType: '' }))}>
                <SelectTrigger className="mt-1" data-testid="contact-doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.docType === 'Custom' && (
              <div>
                <Label>Custom Type Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.customType}
                  onChange={e => setForm(p => ({ ...p, customType: e.target.value }))}
                  placeholder="Describe the document type"
                  className="mt-1"
                  data-testid="contact-doc-custom-type-input"
                />
              </div>
            )}
            <div className={form.docType === 'Custom' ? '' : 'col-span-2'}>
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any notes about this document..."
                rows={2}
                className="mt-1 resize-none"
                data-testid="contact-doc-notes-input"
              />
            </div>
          </div>

          {/* File Drop Zone */}
          {form.file ? (
            <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
              <FileText className="h-4 w-4 text-slate-500 shrink-0" />
              <span className="text-sm text-slate-700 flex-1 truncate">{form.file.name}</span>
              <button type="button" onClick={() => setForm(p => ({ ...p, file: null }))} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <label
              className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg p-5 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
              data-testid="contact-doc-drop-zone"
            >
              <Upload className="h-6 w-6 text-slate-400 mb-1.5" />
              <span className="text-sm font-medium text-slate-600">Click to select file</span>
              <span className="text-xs text-slate-400 mt-0.5">PDF, Word, Excel, images — any format</span>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={e => setForm(p => ({ ...p, file: e.target.files[0] || null }))}
                data-testid="contact-doc-file-input"
              />
            </label>
          )}

          <Button
            onClick={handleUpload}
            disabled={uploading || !form.file || !form.docName.trim()}
            className="w-full"
            data-testid="contact-doc-upload-btn"
          >
            {uploading
              ? <><div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />Uploading...</>
              : <><Upload className="h-4 w-4 mr-2" />Upload Document</>}
          </Button>
        </div>
      )}

      {/* Document List */}
      {loading ? (
        <div className="text-center py-8 text-slate-400 text-sm">Loading documents...</div>
      ) : docs.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No documents yet</p>
          <p className="text-xs mt-1">Click "Add Document" to attach files to this contact</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map(doc => (
            <div
              key={doc.id}
              className="flex items-start gap-3 border rounded-lg px-4 py-3 bg-white hover:bg-slate-50 transition-colors"
              data-testid={`contact-doc-row-${doc.id}`}
            >
              <FileText className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-800 text-sm truncate">{doc.document_name}</span>
                  <Badge variant="outline" className="text-xs shrink-0">{doc.document_type}</Badge>
                </div>
                {doc.notes && <p className="text-xs text-slate-500 mt-0.5 truncate">{doc.notes}</p>}
                <p className="text-xs text-slate-400 mt-0.5">
                  Uploaded {formatDate(doc.upload_date)}
                  {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDownload(doc)}
                  title="Download"
                  data-testid={`download-doc-${doc.id}`}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(doc)}
                  title="Delete"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  data-testid={`delete-doc-${doc.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
